import { useEffect, useRef } from 'react';

interface Snowflake {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
}

export function ChristmasEffect() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const snowflakesRef = useRef<Snowflake[]>([]);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create snowflakes
    const createSnowflake = (): Snowflake => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.5 + 0.5,
    });

    // Initialize snowflakes
    const snowflakeCount = Math.floor((canvas.width * canvas.height) / 15000);
    snowflakesRef.current = Array.from({ length: snowflakeCount }, createSnowflake);

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#ffffff';
      snowflakesRef.current.forEach((flake) => {
        ctx.globalAlpha = flake.opacity;
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.size, 0, Math.PI * 2);
        ctx.fill();

        // Update position
        flake.y += flake.speed;
        flake.x += Math.sin(flake.y * 0.01) * 0.5; // Gentle swaying

        // Reset if off screen
        if (flake.y > canvas.height) {
          flake.y = -10;
          flake.x = Math.random() * canvas.width;
        }
        if (flake.x < 0) flake.x = canvas.width;
        if (flake.x > canvas.width) flake.x = 0;
      });

      ctx.globalAlpha = 1;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-10"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}

// Christmas lights decoration component
export function ChristmasLights() {
  const colors = ['#ff0000', '#00ff00', '#ffff00', '#ff00ff', '#00ffff'];
  const lightCount = 200; // More lights for better coverage
  
  useEffect(() => {
    // Inject CSS animations into the document
    const styleId = 'christmas-lights-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes christmas-twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }
        .christmas-light {
          animation: christmas-twinkle 2s ease-in-out infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 h-6 z-[100] pointer-events-none overflow-hidden flex items-center justify-center"
      style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), transparent)',
      }}
    >
      <div className="flex gap-3 w-full justify-center">
        {Array.from({ length: lightCount }).map((_, i) => {
          const color = colors[i % colors.length];
          const delay = Math.random() * 2;
          return (
            <div
              key={i}
              className="christmas-light rounded-full"
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: color,
                boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
                animationDelay: `${delay}s`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
